import { useState } from 'react';
import { CheckCircle, AlertTriangle, Send } from 'lucide-react';
import { StatusBadge } from './StatusBadge';

interface AprovacaoBarProps {
  status: string;
  onAprovar: (observacoes?: string) => Promise<void>;
  onSolicitarRevisao: (observacoes: string) => Promise<void>;
}

export function AprovacaoBar({ status, onAprovar, onSolicitarRevisao }: AprovacaoBarProps) {
  const [showRevisao, setShowRevisao] = useState(false);
  const [observacoes, setObservacoes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAprovar = async () => {
    setLoading(true);
    try {
      await onAprovar();
    } finally {
      setLoading(false);
    }
  };

  const handleSolicitarRevisao = async () => {
    if (!observacoes.trim()) return;
    setLoading(true);
    try {
      await onSolicitarRevisao(observacoes.trim());
      setObservacoes('');
      setShowRevisao(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-3">
      {showRevisao ? (
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Descreva o motivo da revisao:
            </label>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={2}
              autoFocus
              placeholder="Explique quais pontos precisam ser revisados..."
              className="w-full text-sm px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
            />
          </div>
          <div className="flex gap-2 pb-0.5">
            <button
              onClick={() => { setShowRevisao(false); setObservacoes(''); }}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400"
            >
              Cancelar
            </button>
            <button
              onClick={handleSolicitarRevisao}
              disabled={!observacoes.trim() || loading}
              className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-4 h-4" />
              {loading ? 'Enviando...' : 'Enviar Solicitacao'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
            <StatusBadge status={status} />
          </div>

          {status === 'pendente' && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowRevisao(true)}
                disabled={loading}
                className="flex items-center gap-1.5 px-4 py-2 border border-red-300 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
              >
                <AlertTriangle className="w-4 h-4" />
                Solicitar Revisao
              </button>
              <button
                onClick={handleAprovar}
                disabled={loading}
                className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                {loading ? 'Aprovando...' : 'Aprovar'}
              </button>
            </div>
          )}

          {status === 'revisao_solicitada' && (
            <span className="text-sm text-amber-600 dark:text-amber-400">
              Aguardando revisao pela equipe
            </span>
          )}

          {status === 'aprovado' && (
            <span className="text-sm text-green-600 dark:text-green-400">
              Documento aprovado
            </span>
          )}
        </div>
      )}
    </div>
  );
}
